import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { usePlan } from '../hooks/usePlan';
import { createProject, listProjects } from '../services/projects';
import Layout from '../components/Layout';
import { trackEvent } from '../firebase';
import * as Sentry from '@sentry/react';

export default function NewProject() {
  const { user } = useAuth();
  const { limits } = usePlan();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [atLimit, setAtLimit] = useState(false);
  const [checkingLimit, setCheckingLimit] = useState(true);

  useEffect(() => {
    if (!user) return;
    listProjects(user.uid)
      .then((projects) => {
        if (projects.length >= limits.maxProjects) setAtLimit(true);
      })
      .catch(() => {
        // If we can't check, allow the form to render and let the submit fail.
      })
      .finally(() => setCheckingLimit(false));
  }, [user, limits.maxProjects]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setError(null);
    setLoading(true);
    try {
      const id = await createProject(user.uid, name, description);
      trackEvent('create_project_submitted', { project_id: id });
      navigate(`/projects/${id}`);
    } catch (err: unknown) {
      Sentry.captureException(err);
      setError((err as { message?: string }).message ?? 'Failed to create project.');
      setLoading(false);
    }
  };

  if (!checkingLimit && atLimit) {
    return (
      <Layout>
        <div className="max-w-lg mx-auto px-4 sm:px-6 py-16 text-center">
          <div className="bg-white rounded-xl p-8 shadow-sm flex flex-col items-center gap-4">
            <Lock size={40} className="text-brand-500" />
            <h2 className="text-xl font-bold text-gray-900 m-0">Free plan: 1 waitlist included</h2>
            <p className="text-sm text-gray-500 max-w-xs m-0">
              You&apos;ve already created your free waitlist. Upgrade to Pro for unlimited
              waitlists, higher subscriber limits, and advanced integrations.
            </p>
            <Link
              to="/profile"
              className="inline-flex items-center px-5 py-2.5 bg-brand-500 hover:bg-brand-600 text-white text-sm font-semibold rounded-lg transition-colors mt-2"
              onClick={() => trackEvent('upgrade_cta_clicked', { source: 'new_project_gate' })}
            >
              Upgrade to Pro →
            </Link>
            <Link to="/" className="text-sm text-brand-500 hover:underline">
              ← Back to dashboard
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-lg mx-auto px-4 sm:px-6 py-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">New Waitlist</h2>
        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-5 bg-white rounded-xl p-6 sm:p-8 shadow-sm"
        >
          <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
            Name
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="My Awesome Product"
              className="border border-gray-300 rounded-lg px-3 py-2.5 text-base font-[inherit] outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition-colors"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
            Description
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What are people signing up for?"
              rows={3}
              className="border border-gray-300 rounded-lg px-3 py-2.5 text-base font-[inherit] outline-none resize-y focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition-colors"
            />
          </label>
          {error && <p className="text-red-500 text-sm m-0">{error}</p>}
          <button
            type="submit"
            disabled={loading || checkingLimit}
            className="py-2.5 bg-brand-500 hover:bg-brand-600 text-white border-0 rounded-lg text-base font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? 'Creating…' : 'Create Waitlist'}
          </button>
        </form>
      </div>
    </Layout>
  );
}
